import { useEffect, useState, type FocusEvent } from 'react';
import { formatInrGrouped, parseInrInput } from '@/lib/format';

interface CurrencyInputProps {
  id?: string;
  /** Current numeric value, or undefined/empty if not yet entered. Always a plain number — never a formatted string. */
  value: number | undefined;
  /** Called with the parsed numeric value (or undefined if the field is empty/invalid) on every change. */
  onChange: (value: number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Whether to show 2 decimal places when formatted (blur state). Defaults to true since money amounts commonly carry paise. */
  precise?: boolean;
}

/**
 * The single reusable money-entry input for the whole app (per the Phase
 * 1 correction requirement: "Avoid introducing multiple competing
 * currency-formatting implementations; create/reuse one utility where
 * appropriate."). Every amount field — loan setup, every event form —
 * should use this component rather than a bare `<input type="number">`.
 *
 * UX pattern (mobile-friendly, avoids cursor-jump bugs common to
 * live-reformatting-while-typing inputs):
 *   - While focused/being edited: shows the plain digits the user is
 *     typing, unformatted, with a numeric-friendly keyboard
 *     (inputMode="decimal"). No commas inserted mid-typing.
 *   - On blur: reformats to Indian (lakh/crore) grouped display, e.g.
 *     "35,00,000" or "35,00,000.50". The ₹ symbol is shown as a fixed
 *     visual prefix, not part of the editable text.
 *   - On focus: switches back to the plain, editable representation so
 *     the user can continue editing without fighting inserted commas.
 *
 * The value passed to onChange is ALWAYS a plain number (or undefined),
 * never the formatted string — the formatted string is a pure display
 * concern local to this component, exactly as the correction requires
 * ("The underlying stored/database value must remain a numeric monetary
 * value, NOT the formatted string.").
 */
export function CurrencyInput({ id, value, onChange, placeholder, disabled, precise = true }: CurrencyInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [rawText, setRawText] = useState<string>(value !== undefined ? String(value) : '');

  // Keep the input's displayed text in sync when the value changes from
  // OUTSIDE this component (e.g. an auto-calculated EMI updating this
  // field's value while the user is looking at a different field) —
  // but only while not focused, so we never yank text out from under an
  // actively-typing user.
  useEffect(() => {
    if (!isFocused) {
      setRawText(value !== undefined ? formatInrGrouped(value, precise) : '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isFocused]);

  function handleFocus() {
    setIsFocused(true);
    setRawText(value !== undefined ? String(value) : '');
  }

  function handleBlur(_e: FocusEvent<HTMLInputElement>) {
    setIsFocused(false);
    setRawText(value !== undefined ? formatInrGrouped(value, precise) : '');
  }

  function handleChange(text: string) {
    setRawText(text);
    onChange(parseInrInput(text));
  }

  return (
    <div className="currency-input-wrapper">
      <span className="currency-input-prefix">₹</span>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        className="currency-input-field"
        value={rawText}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={(e) => handleChange(e.target.value)}
      />
    </div>
  );
}
